import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"

export interface DashboardStats {
  deals: {
    open_count: number
    open_value: number
    won_this_month: number
    won_value_this_month: number
    lost_this_month: number
  }
  tasks: {
    overdue: number
    due_today: number
    upcoming_7d: number
    completed_this_week: number
  }
  activities: {
    this_week: number
    today: number
  }
  companies: {
    total: number
    new_this_month: number
  }
  contacts: {
    total: number
    new_this_month: number
  }
  invoices: {
    overdue_count: number
    overdue_value: number
    unpaid_count: number
    unpaid_value: number
  }
  goals: {
    active: number
    on_track: number
    at_risk: number
    completed: number
  }
  service_tickets: {
    open: number
    in_progress: number
    resolved_this_week: number
  }
}

export interface FunnelPhase {
  id: string
  name: string
  color: string
  sort_order: number
  is_won: boolean
  is_lost: boolean
  deal_count: number
  total_value: number
}

export interface FunnelDealType {
  deal_type_id: string
  deal_type_name: string
  phases: FunnelPhase[]
  won: { count: number; value: number }
  lost: { count: number; value: number }
}

export interface ActivityFeedItem {
  id: string
  activity_type: string
  subject: string | null
  description: string | null
  activity_date: string
  entity_type: string | null
  entity_id: string | null
  user: {
    id: string
    first_name: string
    last_name: string
    avatar_url: string | null
  } | null
  metadata: Record<string, unknown>
}

export interface RevenueTrendPoint {
  month: string
  month_label: string
  revenue: number
  deal_count: number
}

export function useDashboardStats() {
  return useQuery({
    queryKey: ["dashboard_stats"],
    queryFn: () => api.get<{ data: DashboardStats }>("/api/v1/dashboard/stats"),
    refetchInterval: 60_000, // auto-refresh every 60s
  })
}

export function useDashboardFunnel() {
  return useQuery({
    queryKey: ["dashboard_funnel"],
    queryFn: () => api.get<{ data: FunnelDealType[] }>("/api/v1/dashboard/funnel"),
    refetchInterval: 60_000,
  })
}

export function useDashboardActivities() {
  return useQuery({
    queryKey: ["dashboard_activities"],
    queryFn: () => api.get<{ data: ActivityFeedItem[] }>("/api/v1/dashboard/activities"),
    refetchInterval: 30_000,
  })
}

export function useDashboardRevenueTrend() {
  return useQuery({
    queryKey: ["dashboard_revenue_trend"],
    queryFn: () => api.get<{ data: RevenueTrendPoint[] }>("/api/v1/dashboard/revenue_trend"),
    refetchInterval: 60_000,
  })
}

export function useQuickCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (title: string) =>
      api.post("/api/v1/tasks", {
        title,
        priority: "normal",
        status: "open",
        due_date: new Date().toISOString().slice(0, 10),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] })
      qc.invalidateQueries({ queryKey: ["dashboard_stats"] })
    },
  })
}
